import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppState, type AppStateStatus, StyleSheet, Text, View } from '@/ui';
import { BrandButton } from '@/components/ui/BrandButton';
import { FingerprintIcon } from '@/components/ui/icons';
import { useAuthStore } from '@/features/auth/authStore';
import { useSettingsStore } from '@/features/settings/settingsStore';
import { tokens } from '@/theme/tokens';
import { useAppTheme, type ThemeColors } from '@/theme/ThemeProvider';
import { authenticate } from './biometric';

/**
 * Full-screen overlay that re-locks the app behind Face ID / fingerprint whenever it
 * returns from the background, if the fan opted into biometric unlock in Settings.
 * Renders nothing when the feature is off or the fan isn't signed in.
 */
export function LockGate() {
  const { t } = useTranslation('me');
  const { colors: C } = useAppTheme();
  const styles = useMemo(() => createStyles(C), [C]);
  const status = useAuthStore((s) => s.status);
  const biometricLock = useSettingsStore((s) => s.biometricLock);
  const armed = status === 'authenticated' && biometricLock;

  const [locked, setLocked] = useState(armed);
  const [authenticating, setAuthenticating] = useState(false);
  const appState = useRef(AppState.currentState);
  const wasArmed = useRef(armed);

  // Lock immediately the moment biometric lock becomes active — covers both cold
  // start (session restored + setting already on) and toggling the setting on live.
  useEffect(() => {
    if (armed && !wasArmed.current) setLocked(true);
    if (!armed) setLocked(false);
    wasArmed.current = armed;
  }, [armed]);

  useEffect(() => {
    if (!armed) return;
    const sub = AppState.addEventListener('change', (next: AppStateStatus) => {
      if (appState.current.match(/active/) && next.match(/inactive|background/)) {
        setLocked(true);
      }
      appState.current = next;
    });
    return () => sub.remove();
  }, [armed]);

  const tryUnlock = async () => {
    setAuthenticating(true);
    try {
      const ok = await authenticate(t('biometric.prompt'));
      if (ok) setLocked(false);
    } finally {
      setAuthenticating(false);
    }
  };

  useEffect(() => {
    if (locked && armed && !authenticating) {
      void tryUnlock();
    }
    // Only auto-prompt on the transition into locked — not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, armed]);

  if (!armed || !locked) return null;

  return (
    <View style={styles.fill}>
      <View style={styles.iconWrap}>
        <FingerprintIcon size={40} color={tokens.accentMint} />
      </View>
      <Text style={styles.title}>{t('biometric.lockedTitle')}</Text>
      <Text style={styles.subtitle}>{t('biometric.lockedSubtitle')}</Text>
      <View style={styles.btnWrap}>
        <BrandButton variant="primary" disabled={authenticating} onPress={tryUnlock}>
          {authenticating ? t('biometric.unlocking') : t('biometric.unlock')}
        </BrandButton>
      </View>
    </View>
  );
}

function createStyles(C: ThemeColors) {
  return StyleSheet.create({
    fill: {
      ...StyleSheet.absoluteFill,
      backgroundColor: C.appBg,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
      zIndex: 1000,
      elevation: 1000,
    },
    iconWrap: {
      width: 76,
      height: 76,
      borderRadius: 38,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(127,203,168,0.12)',
      marginBottom: 18,
    },
    title: { fontFamily: 'Marcellus', fontSize: 22, color: C.text, textAlign: 'center' },
    subtitle: { fontSize: 13.5, color: C.textDim, textAlign: 'center', marginTop: 8, lineHeight: 19 },
    btnWrap: { alignSelf: 'stretch', marginTop: 28 },
  });
}
